// Auto-förslag för etapp- och områdesmärkning (Dok 06).
// Gissar etapp och område utifrån rubriker/namn i dokumenten, samt datum.
// Förslagen sparas aldrig automatiskt – användaren bekräftar dem.

export interface SuggestStage {
  id: string;
  name: string;
  start_date?: string | null;
  end_date?: string | null;
}

export interface Suggestion<T = string> {
  value: T;
  reason: string;
}

const norm = (s: string) =>
  (s || "")
    .toLowerCase()
    .replace(/[_/\-–—]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Plockar ut kännetecken som "etapp 2", "hus a", "plan 3", "bv", "hus 4". */
const tokens = (s: string): string[] => {
  const t = norm(s);
  const out: string[] = [];
  const patterns = [
    /etapp\s*([0-9]+|[a-zäöå])\b/g,
    /skede\s*([0-9]+|[a-zäöå])\b/g,
    /hus\s*([0-9]+|[a-zäöå])\b/g,
    /byggnad\s*([0-9]+|[a-zäöå])\b/g,
    /plan\s*([0-9]+|bv|kv)\b/g,
    /vån(?:ing)?\s*([0-9]+)\b/g,
    /del\s*([0-9]+|[a-zäöå])\b/g,
    /zon\s*([0-9]+|[a-zäöå])\b/g,
  ];
  for (const re of patterns) {
    let m: RegExpExecArray | null;
    while ((m = re.exec(t))) out.push(`${m[0].split(/\s+/)[0]}:${m[1]}`);
  }
  return out;
};

const overlaps = (
  aStart?: string | null,
  aEnd?: string | null,
  bStart?: string | null,
  bEnd?: string | null
) => {
  if (!aStart && !aEnd) return false;
  if (!bStart && !bEnd) return false;
  const as = aStart || aEnd!;
  const ae = aEnd || aStart!;
  const bs = bStart || bEnd!;
  const be = bEnd || bStart!;
  return as <= be && bs <= ae;
};

/**
 * Föreslår etapp för ett dokument utifrån dess namn/område och ev. datum.
 */
export const suggestStage = (
  text: string,
  stages: SuggestStage[],
  dates?: { start?: string | null; end?: string | null }
): Suggestion | null => {
  if (!stages.length) return null;
  const t = norm(text);
  const docTokens = tokens(text);

  let best: { stage: SuggestStage; score: number; reason: string } | null = null;

  for (const stage of stages) {
    const sName = norm(stage.name);
    if (!sName) continue;
    let score = 0;
    let reason = "";

    if (t && sName && t.includes(sName)) {
      score += 5;
      reason = `Namnet innehåller "${stage.name}"`;
    }

    const shared = tokens(stage.name).filter((x) => docTokens.includes(x));
    if (shared.length) {
      score += 4 * shared.length;
      reason = reason || `Matchar "${stage.name}"`;
    }

    // Ordmatchning på meningsbärande ord (minst 4 tecken).
    const words = sName.split(" ").filter((w) => w.length >= 4);
    const hits = words.filter((w) => t.includes(w));
    if (hits.length) {
      score += hits.length;
      reason = reason || `Delar ord med "${stage.name}"`;
    }

    if (dates && overlaps(dates.start, dates.end, stage.start_date, stage.end_date)) {
      score += 2;
      reason = reason || `Datumen ligger inom "${stage.name}"`;
    }

    if (score > 0 && (!best || score > best.score)) best = { stage, score, reason };
  }

  if (!best || best.score < 2) return null;
  return { value: best.stage.id, reason: best.reason };
};

/**
 * Föreslår område (arbetsplats) för en arbetsberedning utifrån namnet,
 * matchat mot kända arbetsområden i projektet.
 */
export const suggestArea = (text: string, areaNames: string[]): Suggestion | null => {
  const t = norm(text);
  if (!t) return null;
  const docTokens = tokens(text);
  let best: { name: string; score: number } | null = null;

  for (const name of areaNames) {
    const n = norm(name);
    if (!n) continue;
    let score = 0;
    if (t.includes(n)) score += 5;
    const shared = tokens(name).filter((x) => docTokens.includes(x));
    score += 3 * shared.length;
    const words = n.split(" ").filter((w) => w.length >= 4);
    score += words.filter((w) => t.includes(w)).length;
    if (score > 0 && (!best || score > best.score)) best = { name, score };
  }

  if (!best || best.score < 3) return null;
  return { value: best.name, reason: `Namnet pekar mot "${best.name}"` };
};

/** Föreslår datum för ett arbetsområde utifrån etappens datum. */
export const suggestDatesFromStage = (
  stage?: SuggestStage | null
): { start: string; end: string } | null => {
  if (!stage?.start_date && !stage?.end_date) return null;
  return { start: stage?.start_date || "", end: stage?.end_date || "" };
};
